import type {
  PartnerContract,
  PartnerContractCreate,
  PartnerContractUpdate,
  PartnerSearchContractsParams,
} from '@shipium-corp/shipium-ts-types';
import GatewayAxiosClient from '~/store/services/GatewayAxiosClient';
import { linkMultipleAssets, type LinkAssetPayload, type BulkLinkPayloads } from '../api-utils';

type SingleAssetLinkType = 'carrierSettings' | 'carrierRateSets' | 'carrierAccounts';
type ArrayAssetLinkType = 'tags';

export const contracts = {
  search: {
    queryKey: 'partner-management-service/contracts',
    configurationType: 'cached' as const,
    fn: async (params: PartnerSearchContractsParams = {}) => {
      const { data } = await GatewayAxiosClient.get<PartnerContract[]>(
        '/partner-management-proxy/api/v1/partners/contracts',
        { params },
      );

      return data;
    },
  },
  get: {
    queryKey: 'partner-management-service/contracts/id',
    configurationType: 'no-cache' as const,
    fn: async (contractId: string) => {
      const { data } = await GatewayAxiosClient.get<PartnerContract>(
        `/partner-management-proxy/api/v1/partners/contracts/${contractId}`,
      );

      return data;
    },
  },
  create: async (payload: PartnerContractCreate) => {
    const { data } = await GatewayAxiosClient.post<PartnerContract>(
      '/partner-management-proxy/api/v1/partners/contracts',
      payload,
    );

    return { data, invalidateQueryKeys: [['partner-management-service/contracts']] };
  },
  update: async ({ contractId, payload }: { contractId: string; payload: PartnerContractUpdate }) => {
    const { data } = await GatewayAxiosClient.put<PartnerContract>(
      `/partner-management-proxy/api/v1/partners/contracts/${contractId}`,
      payload,
    );

    return {
      data,
      invalidateQueryKeys: [
        ['partner-management-service/contracts'],
        ['partner-management-service/contracts/id', contractId],
      ],
    };
  },
  link: async ({
    contractId,
    payload,
  }: {
    contractId: string;
    payload: LinkAssetPayload<SingleAssetLinkType | ArrayAssetLinkType>;
  }) => {
    await GatewayAxiosClient({
      method: payload.linkAction === 'link' ? 'PUT' : 'DELETE',
      url:
        `/partner-management-proxy/api/v1/partners/contracts/` +
        `${contractId}/${payload.assetType}/` +
        `${payload.assetId}`,
    });

    return {
      invalidateQueryKeys: [
        ['partner-management-service/contracts'],
        ['partner-management-service/contracts/id', contractId],
      ],
    };
  },
  bulkLink: async ({
    contract,
    payloads,
  }: {
    contract: PartnerContract;
    payloads: BulkLinkPayloads<SingleAssetLinkType, ArrayAssetLinkType>;
  }) => {
    return await linkMultipleAssets<SingleAssetLinkType, ArrayAssetLinkType, PartnerContract>(
      payloads,
      {
        tags: contract.tagIds,
        carrierSettings: contract.carrierSettingsId,
        carrierRateSets: contract.carrierRateSetId,
        carrierAccounts: contract.carrierAccountId,
      },
      (payload) => contracts.link({ contractId: contract.contractId!, payload }),
    );
  },
};
